import { createSlice } from "@reduxjs/toolkit";
import { getNurseRequests, getNurseSessions, getNurseRequestDetails, getNurseSessionDetails, verifyNurseRequest, getRequestSessions, assignSessionNurse, assignNurseRequest, getSpecimenLogs, getPatientHistory } from "./manageRequestThunk";
import { sortByReceivingTimeDesc } from "./requestListUtils";

const initialState = {
  newRequests: [],
  approvedRequests: [],
  scheduledRequests: [],
  upcomingRequests: [],
  pastRequests: [],
  tabCounts: { new: 0, approved: 0, scheduled: 0, upcoming: 0, past: 0 },
  totalPages: 0,
  totalDocuments: 0,
  currentPage: 1,
  requestDetails: null,
  sessionDetails: null,
  requestSessions: [],
  specimenLogs: [],
  specimenTotalPages: 0,
  specimenTotalDocuments: 0,
  specimenCurrentPage: 1,
  patientHistoryData: null,
  error: null,
};

const manageRequestSlice = createSlice({
  name: "manageRequest",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getNurseRequests.fulfilled, (state, action) => {
      const { tab, data, countOnly } = action.payload;

      if (countOnly) {
        state.tabCounts[tab] = data?.totalDocuments || 0;
        return;
      }

      const sortedRequests = sortByReceivingTimeDesc(data.data || []);
      if (tab === "new") state.newRequests = sortedRequests;
      if (tab === "approved") state.approvedRequests = sortedRequests;
      if (tab === "scheduled") state.scheduledRequests = sortedRequests;

      state.totalPages = data.totalPages || 0;
      state.totalDocuments = data.totalDocuments || sortedRequests.length;
      state.currentPage = data.currentPage || 1;
    });

    builder.addCase(getNurseSessions.fulfilled, (state, action) => {
      const { tab, data, countOnly } = action.payload;

      if (countOnly) {
        state.tabCounts[tab] = data?.totalDocuments || 0;
        return;
      }

      const sortedSessions = sortByReceivingTimeDesc(data.sessions || []);
      if (tab === "upcoming") state.upcomingRequests = sortedSessions;
      if (tab === "past") state.pastRequests = sortedSessions;

      state.totalPages = data.totalPages || 0;
      state.totalDocuments = data.totalDocuments || sortedSessions.length;
      state.currentPage = data.currentPage || 1;
    });

    builder.addCase(getNurseRequestDetails.fulfilled, (state, action) => {
      state.requestDetails = action.payload.data?.request || null;
    });

    builder.addCase(getNurseSessionDetails.fulfilled, (state, action) => {
      state.sessionDetails = action.payload.data?.session || null;
    });

    builder.addCase(verifyNurseRequest.fulfilled, (state, action) => {
      state.requestDetails = action.payload.data?.request || state.requestDetails;
    });

    builder.addCase(assignNurseRequest.fulfilled, (state, action) => {
      state.requestDetails = action.payload.data?.request || state.requestDetails;
    });

    builder.addCase(getRequestSessions.pending, (state) => {
      state.requestSessions = [];
    });

    builder.addCase(getRequestSessions.fulfilled, (state, action) => {
      state.requestSessions = action.payload?.data?.sessions || [];
    });

    builder.addCase(getPatientHistory.pending, (state) => {
      state.patientHistoryData = null;
    });

    builder.addCase(getPatientHistory.fulfilled, (state, action) => {
      state.patientHistoryData = action.payload?.data || null;
    });

    builder.addCase(getSpecimenLogs.fulfilled, (state, action) => {
      const { data } = action.payload;
      state.specimenLogs = data?.specimens || [];
      state.specimenTotalPages = data?.totalPages || 0;
      state.specimenTotalDocuments = data?.totalDocuments || 0;
      state.specimenCurrentPage = data?.currentPage || 1;
    });

    // Backend returns { assigned: true } — no session object.
    // Use meta.arg.sessionId to locate the row and update status to ASSIGNED locally.
    builder.addCase(assignSessionNurse.fulfilled, (state, action) => {
      if (action.payload?.data?.assigned) {
        const { sessionId, nurse } = action.meta.arg;
        const idx = state.requestSessions.findIndex((s) => s._id === sessionId);
        if (idx !== -1) {
          state.requestSessions[idx] = { ...state.requestSessions[idx], status: "ASSIGNED", nurse: nurse || state.requestSessions[idx].nurse };
        }
      }
    });
  },
});

export default manageRequestSlice.reducer;
